import { getSaasApiBaseUrlForOrgSettings } from '../dateRange/orgDefaultDateRange';

function getAuthHeaders() {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function formatApiError(payload) {
  const d = payload?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === 'object' && x?.msg ? x.msg : String(x))).join(' ');
  }
  return payload?.message || 'Request failed';
}

/**
 * Email a report summary via SaaS Foundation POST /reports/share/email
 */
export async function sendReportEmail(payload) {
  const res = await fetch(`${getSaasApiBaseUrlForOrgSettings()}/reports/share/email`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

export const REPORT_SERVICE_KEYS = {
  EXECUTIVE: 'executive',
  FINANCE: 'finance',
  DENIAL_MANAGEMENT: 'denial_management',
  CLAIMS_PROCESSING: 'claims_processing',
  AR_POSTING: 'ar_posting',
  CHARGE_MANAGEMENT: 'charge_management',
  PRIOR_AUTHORIZATION: 'prior_authorization',
  INSURANCE_VERIFICATION: 'insurance_verification',
  REMITTANCE_ADVICE: 'remittance_advice',
  EDI_PLATFORM: 'edi_platform',
  PATIENT_BILLING: 'patient_billing',
};

export function buildReportShareEmailPayload({ organizationId, context, recipients, subject, message }) {
  const ctx = context || {};
  return {
    organization_id: organizationId,
    recipients,
    subject: subject || `${ctx.report_name || 'Report'}${ctx.report_period ? ` — ${ctx.report_period}` : ''}`,
    message,
    service_key: ctx.service_key,
    report_name: ctx.report_name || 'Report',
    report_period: ctx.report_period || null,
    report_url: ctx.report_url || null,
    metrics: ctx.metrics || [],
    executive_report_id: ctx.executive_report_id || null,
  };
}

export function buildExecutiveReportEmailPayload({
  organizationId,
  reportName,
  reportPeriod,
  draft,
  executiveReportId,
  recipients,
  subject,
  message,
}) {
  const payload = draft?.payload || draft || {};
  const metrics = (payload.topKpis || [])
    .filter((kpi) => kpi?.label)
    .slice(0, 20)
    .map((kpi) => ({
      label: kpi.label,
      value: formatKpiDisplay(kpi),
    }));

  return {
    organization_id: organizationId,
    recipients,
    subject: subject || `Executive KPI: ${reportName}`,
    message,
    service_key: REPORT_SERVICE_KEYS.EXECUTIVE,
    report_name: reportName,
    report_period: reportPeriod || payload.period || null,
    metrics,
    executive_report_id: executiveReportId || null,
  };
}

export function buildExecutiveShareContext({
  reportName,
  reportPeriod,
  draft,
  executiveReportId,
}) {
  const payload = draft?.payload || draft || {};
  const metrics = (payload.topKpis || [])
    .filter((kpi) => kpi?.label)
    .slice(0, 20)
    .map((kpi) => ({
      label: kpi.label,
      value: formatKpiDisplay(kpi),
    }));
  const summary = payload.monthlySummary || {};
  for (const [key, label] of [
    ['daysInAr', 'Days in A/R'],
    ['netCollectionRate', 'Net collection rate'],
    ['denialRate', 'Denial rate'],
  ]) {
    if (summary[key] != null) {
      metrics.push({ label, value: String(summary[key]) });
    }
  }
  return {
    service_key: REPORT_SERVICE_KEYS.EXECUTIVE,
    report_name: reportName,
    report_period: reportPeriod || payload.period || null,
    executive_report_id: executiveReportId || null,
    metrics,
  };
}

export function buildFinanceShareContext({
  reportName,
  reportPeriod,
  metrics = [],
}) {
  return {
    service_key: REPORT_SERVICE_KEYS.FINANCE,
    report_name: reportName || 'Finance Report',
    report_period: reportPeriod || null,
    metrics: (metrics || []).slice(0, 20),
  };
}

export function buildDenialShareContext({ preview, dateRangeLabel, reportName }) {
  const summary = preview?.summary || {};
  return {
    service_key: REPORT_SERVICE_KEYS.DENIAL_MANAGEMENT,
    report_name: reportName || 'Denial Summary Report',
    report_period:
      dateRangeLabel
      || (summary.start_date && summary.end_date
        ? `${summary.start_date.slice(0, 10)} → ${summary.end_date.slice(0, 10)}`
        : null),
    metrics: [
      { label: 'Total denials', value: String(summary.total_denials ?? 0) },
      {
        label: 'Total amount',
        value: `$${Number(summary.total_amount || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      },
    ],
  };
}

/** @deprecated Use buildDenialShareContext + platform ReportingEmailShell */
export function buildDenialSummaryEmailPayload({
  organizationId,
  preview,
  recipients,
  subject,
  message,
  dateRangeLabel,
}) {
  const summary = preview?.summary || {};
  const metrics = [
    { label: 'Total denials', value: String(summary.total_denials ?? 0) },
    {
      label: 'Total amount',
      value: `$${Number(summary.total_amount || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    },
    {
      label: 'Date range',
      value: dateRangeLabel
        || `${summary.start_date?.slice(0, 10) || '—'} → ${summary.end_date?.slice(0, 10) || '—'}`,
    },
  ];

  return {
    organization_id: organizationId,
    recipients,
    subject: subject || 'Denial Summary Report',
    message,
    service_key: REPORT_SERVICE_KEYS.DENIAL_MANAGEMENT,
    report_name: 'Denial Summary Report',
    report_period: dateRangeLabel || null,
    metrics,
  };
}

function formatKpiDisplay(kpi) {
  if (kpi?.value == null) return kpi?.integrationLabel || '—';
  const v = Number(kpi.value);
  if (Number.isNaN(v)) return String(kpi.value);
  if (kpi.unit === 'currency') return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (kpi.unit === 'percent') return `${v}%`;
  if (kpi.unit === 'days') return `${v} days`;
  return v.toLocaleString();
}
